'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';
import Sidebar from '@/components/ui/Sidebar';
import Header from '@/components/ui/Header';
import MobileTabBar from '@/components/ui/MobileTabBar';
import AgentSlideOut from '@/components/agent/AgentSlideOut';
import ImpersonationBanner from '@/components/ui/ImpersonationBanner';

const PUBLIC_PATHS = ['/login', '/signup', '/auth/callback', '/onboarding', '/welcome', '/privacy', '/terms', '/affiliates', '/help'];

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { user, tenant, loading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  const isPublic = PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'));

  // Detect if we're on the marketing domain (not app subdomain)
  const isMarketingDomain = typeof window !== 'undefined' && !window.location.hostname.startsWith('app.');

  useEffect(() => {
    if (loading) return;

    // On marketing domain, never interfere — middleware handles routing
    if (isMarketingDomain) return;

    if (!user && !isPublic) {
      router.replace('/login');
      return;
    }
  }, [user, tenant, loading, isPublic, router, isMarketingDomain]);

  // Marketing domain: never show sidebar/header, always render children bare
  if (isMarketingDomain) {
    return <>{children}</>;
  }

  // Public pages render without shell
  if (isPublic) {
    return <>{children}</>;
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#fafafa] dark:bg-[#0f0f0f]">
        <div className="w-6 h-6 border-2 border-[#00C2A8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not authed — will redirect above, show nothing
  if (!user) return null;

  // Authenticated on app subdomain — render with sidebar/header
  const isImpersonating = typeof window !== 'undefined' && !!localStorage.getItem('crewsync_impersonate_tenant');

  // On the agent + assistant pages on MOBILE, lock main's scroll so swipes
  // inside the chat bubble don't push the whole page (composer floats away).
  // Desktop keeps overflow-y-auto so its own scrollbars work. Note that the
  // chat container handles its own internal scroll with overscroll-contain.
  const isChat = pathname === '/agent' || pathname.startsWith('/agent/')
    || pathname === '/assistant' || pathname.startsWith('/assistant/');

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <ImpersonationBanner />
      <Sidebar />
      {/* On mobile, leave room at the bottom for the MobileTabBar (56px tab
          height + iOS safe-area inset). Desktop is unaffected. */}
      <main className={`flex-1 ${isChat ? 'overflow-hidden md:overflow-y-auto' : 'overflow-y-auto'} bg-[#fafafa] dark:bg-[#0f0f0f] ${isImpersonating ? 'pt-24 md:pt-10' : 'pt-14 md:pt-0'} flex flex-col pb-[calc(56px+env(safe-area-inset-bottom))] md:pb-[env(safe-area-inset-bottom)]`}>
        <Header />
        <div className="flex-1 min-h-0">
          {children}
        </div>
      </main>
      {/* Bottom tab bar — mobile only. Replaces the slide-out hamburger. */}
      <MobileTabBar />
      {/* Floating agent button + drawer. Hides itself on /agent and /workflows/:id */}
      <AgentSlideOut />
    </div>
  );
}
